import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Printer, CheckCircle2, Lightbulb, HelpCircle } from 'lucide-react';
import { WORKFLOW_GUIDES, guideForRole } from './workflowGuide';

const Screens = ({ screens }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose mb-6 print:grid-cols-2">
    {screens.map((s) => (
      <figure key={s.src} className="m-0 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 break-inside-avoid">
        <a href={s.src} target="_blank" rel="noreferrer" title="Open full size" className="print:pointer-events-none">
          <img src={s.src} alt={s.caption} className="w-full h-auto block hover:opacity-90 transition-opacity" loading="lazy" />
        </a>
        <figcaption className="text-xs text-gray-600 px-3 py-2 border-t border-gray-200">{s.caption}</figcaption>
      </figure>
    ))}
  </div>
);

const Guide = ({ guide }) => (
  <article data-testid={`workflow-guide-${guide.id}`} className="break-before-page first:break-before-auto">
    <h3 className="text-xl font-bold text-gray-900 mb-1">{guide.title}</h3>
    <p className="text-gray-600 mb-4">{guide.tagline}</p>
    <p className="text-xs text-gray-500 mb-4 not-prose">Login role{guide.roles.length > 1 ? 's' : ''}: {guide.roles.map((r) => (r === 'Consultant' ? 'Doctor (General)' : r)).join(', ')}</p>
    <Screens screens={guide.screens} />
    {guide.steps.map((step) => (
      <section key={step.title} className="mb-5 break-inside-avoid">
        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-teal-600" /> {step.title}</h4>
        <ol className="list-decimal list-outside ml-5 space-y-1.5 text-sm text-gray-800">
          {step.items.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      </section>
    ))}
    {guide.tips?.length > 0 && (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5 not-prose break-inside-avoid">
        <p className="font-semibold text-amber-900 mb-1 flex items-center gap-2 text-sm"><Lightbulb className="w-4 h-4" /> Good to know</p>
        <ul className="list-disc list-outside ml-5 space-y-1 text-sm text-amber-900">{guide.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
      </div>
    )}
    {guide.faqs?.length > 0 && (
      <div className="mb-2 break-inside-avoid">
        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-blue-600" /> Common questions</h4>
        <dl className="space-y-2 text-sm">
          {guide.faqs.map(([q, a]) => (
            <div key={q} className="bg-gray-50 border border-gray-200 rounded-lg p-3"><dt className="font-medium text-gray-900">{q}</dt><dd className="text-gray-700 mt-0.5">{a}</dd></div>
          ))}
        </dl>
      </div>
    )}
  </article>
);

// Patient Workflow guide: hospital staff see their own role; the Acacia office can browse every role or print them all.
export const WorkflowGuideSection = ({ username, role, isSuperAdmin }) => {
  const own = guideForRole(username, role);
  const [selected, setSelected] = useState(isSuperAdmin ? 'all' : own.id);
  const guides = selected === 'all' ? WORKFLOW_GUIDES : WORKFLOW_GUIDES.filter((g) => g.id === selected);

  return (
    <div data-testid="workflow-guide">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4 not-prose print:hidden">
        <div className="flex flex-wrap gap-1.5" data-testid="workflow-guide-roles">
          {isSuperAdmin && (
            <>
              <button onClick={() => setSelected('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${selected === 'all' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-testid="workflow-guide-role-all">All roles</button>
              {WORKFLOW_GUIDES.map((g) => (
                <button key={g.id} onClick={() => setSelected(g.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${selected === g.id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-testid={`workflow-guide-role-${g.id}`}>{g.title}</button>
              ))}
            </>
          )}
        </div>
        <Button variant="outline" onClick={() => window.print()} className="flex items-center gap-2" data-testid="workflow-guide-print">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </Button>
      </div>
      <div className="hidden print:block mb-6">
        <h2 className="text-2xl font-bold">Acacia Patient Workflow — User Guide</h2>
        <p className="text-sm text-gray-600">{selected === 'all' ? 'All roles' : guides[0].title} • printed {new Date().toLocaleDateString('en-GB')}</p>
      </div>
      <div className="space-y-10">
        {guides.map((g) => <Guide key={g.id} guide={g} />)}
      </div>
    </div>
  );
};
